/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

import { Injectable } from '@angular/core';
import 'rxjs/add/operator/map';
import {BehaviorSubject, Observable} from "rxjs";
import * as firebase from 'firebase';


import {DataReference, OnlineState} from "../shared/DataReference";
import {FirebaseApp} from "angularfire2";
import {UserModel} from "../models/UserModel";
import {DeviceUtilityService} from "./device-utility-service/device-utility-service";

/*
  Generated class for the AuthService provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular 2 DI.
*/
@Injectable()
export class AuthService {

  private static AUTH_ERR_INVALID_EMAIL: string = 'auth/invalid-email';
  private static AUTH_ERR_USER_DISABLED: string = 'auth/user-disabled';
  private static AUTH_ERR_USER_NOT_FOUND: string = 'auth/user-not-found';
  private static AUTH_ERR_WRONG_PASSWORD: string = 'auth/wrong-password';

  private _userId: BehaviorSubject<string> = new BehaviorSubject(null);
  private _isOnline$ = new BehaviorSubject(false);
  private _email: BehaviorSubject<string> = new BehaviorSubject(null);

  get userId() : Observable<string> {
    return this._userId.asObservable();
  }

  get email(): Observable<string> {
    return this._email.asObservable();
  }

  getCurrentUserId() {
    return this._userId.getValue();
  }

  isOnline$ = this._isOnline$.asObservable();

  constructor(private firebase: FirebaseApp,
              private deviceService: DeviceUtilityService) {
  }

  init() {
    this.listenForAuthChanges();
  }

  login(email: string, password: string) : Promise<string> {

    return new Promise((resolve, reject) => {
      this.firebase.auth().signInWithEmailAndPassword(email, password)
      .catch(err => {
        console.log('login error');
        console.error(err);
        let errorMessage: string = '';

        switch (err['code']) {
          case AuthService.AUTH_ERR_INVALID_EMAIL:
            errorMessage = 'Email is invalid';
            break;
          case AuthService.AUTH_ERR_USER_DISABLED:
            errorMessage = 'There is an issue with your account. Please contact support';
            break;
          case AuthService.AUTH_ERR_USER_NOT_FOUND:
            errorMessage = 'No account exists with this email';
            break;
          case AuthService.AUTH_ERR_WRONG_PASSWORD:
            errorMessage = 'Password is incorrect';
            break;
        }

        // this._userId.error(errorMessage);
        reject(errorMessage);
      }).then(auth => {
        console.log('login complete');
        console.log(auth);
        if (auth) {
          resolve(auth.uid);
        }
      });
    });

  }

  private _createAuthAccount(email: string, password: string) : Promise<string> {
    return new Promise((resolve, reject) => {
      this.firebase.auth().createUserWithEmailAndPassword(email, password)
        .then(auth => {
          if (auth) {
            let userId = auth.uid;
            resolve(userId);
          } else {
            reject('User not created');
          }
        })
        .catch(err => {
          console.error(err.code);
          console.error(err.message);
          reject(err.message);
        });
    });
  }

  createAccount(user: UserModel, password: string) : Promise<string> {
    return new Promise((resolve, reject) => {
      this._createAuthAccount(user.email, password).then(userId => {

        if (userId) {
          this.firebase.database().ref(DataReference.getUserRef(userId)).update(user, err => {
            if (err) {
              console.error(err);
              reject(err);
            } else {
              resolve(userId);
            }
          });
        } else {
          console.warn('user id is missing');
          reject('User ID is missing');
        }

      }, err => {
        console.error(err);
        reject(err);
      });
    });
  }

  logout() {
    return new Promise((resolve, reject) => {
      this.firebase.auth().signOut().then(() => {
        resolve();
      }, err => {
        console.error(err);
        reject(err);
      });
    });
  }

  private listenForAuthChanges() {
    this.firebase.auth().onAuthStateChanged(user => {
      if (user) {
        console.log('signin change');
        // console.log(user);
        // user signed in
        this._userId.next(user.uid);
        this._email.next(user.email);
        this.manageOnlineState(user.uid);
      } else {
        console.log('signout');
        // user signed out
        this._userId.next(null);
        this._email.next(null);
      }
    });
  }

  private manageOnlineState(userId: string) {
    console.log('Managing online state for ' + userId);
    const connectRef = this.firebase.database().ref(DataReference.CONNECTED_REF);

    connectRef.on(DataReference.Value, snapshot => {
      let online = snapshot.val();
      this._isOnline$.next(online);
      console.log('connect value changed');
      console.log(online);
      if (snapshot.val()) {
        const userRef = this.firebase.database().ref(`${DataReference.getUserOnlineRef(userId)}`);
        userRef.onDisconnect().set(OnlineState.Offline, res => {
          console.log('onDisconnect');
          console.log(res);
        });
        userRef.set(OnlineState.Online);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth-service.ts